import React, { useCallback, useMemo, useState } from 'react';

import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material';
import { DataGrid, GridRowSelectionModel } from '@mui/x-data-grid';

import {
  RATE_CODE_INITIAL_SEARCH,
  RateCodeSearchState,
  rateCodeColumns,
} from '~/components/Cruises/components/shared/RateCodeGridConfig';
import RateCodeSearchForm from '~/components/Cruises/components/shared/RateCodeSearchForm';

import { RATE_CODE_PAGE_SIZE, useGetRateCodes } from '~/hooks/apis/cruises/useGetRateCodes';

export interface SelectedRate {
  id: string;
  code: string;
}

interface Props {
  vendorCode: string | undefined;
  value: Array<SelectedRate>;
  onChange: (rates: Array<SelectedRate>) => void;
}

export default function DealRateCodeBulkSelect({ vendorCode, value, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [searchFields, setSearchFields] = useState<RateCodeSearchState>(RATE_CODE_INITIAL_SEARCH);
  const [committedSearch, setCommittedSearch] = useState<RateCodeSearchState>(RATE_CODE_INITIAL_SEARCH);
  const [pendingIds, setPendingIds] = useState<Array<string>>([]);
  const [labelCache, setLabelCache] = useState<Map<string, string>>(new Map());
  const [page, setPage] = useState(0);

  const { data, isFetching, isError } = useGetRateCodes(open ? vendorCode : undefined, committedSearch, page);

  const rows = useMemo(() => data?.result ?? [], [data]);

  const handleOpen = useCallback(() => {
    setPendingIds(value.map((r) => r.id));
    setLabelCache(new Map(value.map((r) => [r.id, r.code])));
    setOpen(true);
  }, [value]);

  const handleClose = useCallback(() => {
    setOpen(false);
    setSearchFields(RATE_CODE_INITIAL_SEARCH);
    setCommittedSearch(RATE_CODE_INITIAL_SEARCH);
  }, []);

  const handleConfirm = useCallback(() => {
    onChange(pendingIds.map((id) => ({ id, code: labelCache.get(id) ?? '' })));
    handleClose();
  }, [pendingIds, labelCache, onChange, handleClose]);

  const handleSelectionChange = useCallback(
    (model: GridRowSelectionModel) => {
      const ids = (Array.isArray(model) ? model : []).map(String);
      setPendingIds(ids);
      setLabelCache((prev) => {
        const next = new Map(prev);
        rows.forEach((row) => next.set(row.id, row.code));
        return next;
      });
    },
    [rows],
  );

  const handleRemove = useCallback(
    (idToRemove: string) => {
      onChange(value.filter((r) => r.id !== idToRemove));
    },
    [value, onChange],
  );

  const handleSearch = useCallback(() => {
    setPage(0);
    setCommittedSearch(searchFields);
  }, [searchFields]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSearch();
      }
    },
    [handleSearch],
  );

  return (
    <Box>
      {value.length > 0 && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
          {value.map((rate) => (
            <Chip key={rate.id} label={rate.code} onDelete={() => handleRemove(rate.id)} size="small" />
          ))}
        </Stack>
      )}

      <Button variant="outlined" onClick={handleOpen} disabled={!vendorCode}>
        {value.length > 0 ? 'Edit Rate Codes' : 'Add Rate Codes'}
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth>
        <DialogTitle>Select Rate Codes</DialogTitle>

        <DialogContent>
          <RateCodeSearchForm
            searchFields={searchFields}
            onSearchFieldsChange={setSearchFields}
            onSearch={handleSearch}
            isSearching={isFetching}
            onKeyDown={handleKeyDown}
          />

          {isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Failed to load rate codes. Please try again.
            </Alert>
          )}

          <DataGrid
            loading={isFetching}
            rows={rows}
            columns={rateCodeColumns}
            getRowId={(row) => row.id}
            checkboxSelection
            rowSelectionModel={pendingIds}
            onRowSelectionModelChange={handleSelectionChange}
            keepNonExistentRowsSelected
            disableRowSelectionOnClick={false}
            pagination
            paginationMode="server"
            rowCount={data?.total ?? 0}
            pageSizeOptions={[RATE_CODE_PAGE_SIZE]}
            paginationModel={{ page, pageSize: RATE_CODE_PAGE_SIZE }}
            onPaginationModelChange={({ page: newPage }) => setPage(newPage)}
            autoHeight
            disableColumnMenu
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirm}>
            {pendingIds.length > 0
              ? `Select ${pendingIds.length} rate code${pendingIds.length > 1 ? 's' : ''}`
              : 'Select rate codes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
